/**
 * Talking Heads canvas designer — model, premade canvases and export.
 *
 * A *design* is what the studio edits:
 *   {
 *     canvasId, name, layoutKind: 'composite' | 'full_avatar' | 'full_content',
 *     layers: { content, avatar, clip }   // canvas px boxes + frame + shadow (null = not used)
 *     contentFit, clipFit,
 *     background: { type, color, gradient, url, tint, pattern, vignette },
 *     decor: [ banner-schema layers + placement ('under'|'over') + scope ('both'|'narration'|'clip') ],
 *   }
 *
 * It is exported as layout_config (style_version 2). Legacy keys are kept so
 * older renderers still produce the same boxes; helper/talking_heads_canvas.py
 * renders the design part (gradients, patterns, rounded/circle frames,
 * shadows, decorations) exactly like ThCanvasScene.jsx draws it.
 */
import { createLayer, newLayerId } from './bannerPresets';

export const TH_STYLE_VERSION = 2;
export const LEGACY_PREVIEW_W = 560; // legacy borders were stored in 560px-preview units

export const OUTPUT_PRESETS = {
  landscape: { w: 1920, h: 1080, label: '16:9', long: '16:9 · 1920×1080' },
  portrait: { w: 1080, h: 1920, label: '9:16', long: '9:16 · 1080×1920' },
  square: { w: 1080, h: 1080, label: '1:1', long: '1:1 · 1080×1080' },
};

export const LAYER_META = {
  content: { label: 'Content', hint: 'Narration footage', color: '#22c55e' },
  avatar: { label: 'Avatar', hint: 'DreamFace talking head', color: '#a78bfa' },
  clip: { label: 'Clip', hint: '[CLIP:…] segments', color: '#60a5fa' },
};

export const FIT_MODES = [
  { value: 'cover', label: 'Cover', title: 'Fill the box — crops edges if needed' },
  { value: 'contain', label: 'Fit', title: 'Whole frame visible — empty sides filled with a blurred copy (no black bars)' },
  { value: 'fill', label: 'Stretch', title: 'Stretch to the exact box' },
];

export const FRAME_STYLES = [
  { value: 'solid', label: 'Solid' },
  { value: 'dashed', label: 'Dashed' },
  { value: 'dotted', label: 'Dotted' },
  { value: 'double', label: 'Double' },
];

export const PATTERNS = [
  { value: 'none', label: 'None' },
  { value: 'grid', label: 'Grid' },
  { value: 'dots', label: 'Dots' },
  { value: 'diagonal', label: 'Diagonal' },
  { value: 'scanlines', label: 'Lines' },
];

export const BG_TYPES = [
  { value: 'gradient', label: 'Gradient' },
  { value: 'solid', label: 'Solid' },
  { value: 'blurred', label: 'Blur' },
  { value: 'image', label: 'Image' },
  { value: 'video', label: 'Video' },
];

/** Quick palettes for the background inspector. */
export const GRADIENT_SWATCHES = [
  { from: '#0b1022', to: '#312e81', angle: 135 },
  { from: '#020617', to: '#0f766e', angle: 160 },
  { from: '#1a0633', to: '#7e22ce', angle: 135 },
  { from: '#0c0a09', to: '#9a3412', angle: 145 },
  { from: '#111827', to: '#be123c', angle: 120 },
  { from: '#0f172a', to: '#0369a1', angle: 180 },
  { from: '#18181b', to: '#3f3f46', angle: 135 },
  { from: '#fdf4ff', to: '#e0e7ff', angle: 135 },
];

export const DEFAULT_FRAME = { width: 0, color: '#ffffff', radius: 0, style: 'solid', shape: 'rect', opacity: 1 };
export const DEFAULT_SHADOW = { enabled: false, blur: 48, x: 0, y: 18, spread: 0, color: '#000000', opacity: 0.45 };
export const DEFAULT_BACKGROUND = {
  type: 'blurred',
  color: '#0b1020',
  gradient: { type: 'linear', angle: 135, from: '#0b1022', to: '#312e81' },
  url: '',
  tint: { color: '#000000', opacity: 0 },
  pattern: { kind: 'none', color: '#ffffff', opacity: 0.06, size: 64, thickness: 2 },
  vignette: 0,
};

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const num = (v, d) => (Number.isFinite(Number(v)) ? Number(v) : d);
const isHex = (c) => typeof c === 'string' && /^#[0-9a-f]{6}$/i.test(c);
const hex = (c, d) => (isHex(c) ? c.toLowerCase() : d);
const clone = (o) => JSON.parse(JSON.stringify(o));

export function orientationOf(w, h) {
  if (w > h * 1.15) return 'landscape';
  if (h > w * 1.15) return 'portrait';
  return 'square';
}

/* ─────────────────────────────────────────────── normalisers ── */
export function normFrame(f) {
  const x = f || {};
  return {
    width: clamp(num(x.width, 0), 0, 80),
    color: hex(x.color, '#ffffff'),
    radius: clamp(num(x.radius, 0), 0, 2000),
    style: FRAME_STYLES.some((s) => s.value === x.style) ? x.style : 'solid',
    shape: x.shape === 'circle' ? 'circle' : 'rect',
    opacity: clamp(num(x.opacity, 1), 0, 1),
  };
}

export function normShadow(s) {
  const x = s || {};
  return {
    enabled: !!x.enabled,
    blur: clamp(num(x.blur, DEFAULT_SHADOW.blur), 0, 300),
    x: clamp(num(x.x, 0), -300, 300),
    y: clamp(num(x.y, DEFAULT_SHADOW.y), -300, 300),
    spread: clamp(num(x.spread, 0), -100, 200),
    color: hex(x.color, '#000000'),
    opacity: clamp(num(x.opacity, DEFAULT_SHADOW.opacity), 0, 1),
  };
}

export function normLayer(l, W, H) {
  if (!l) return null;
  const width = clamp(Math.round(num(l.width, W * 0.5)), 40, W);
  const height = clamp(Math.round(num(l.height, H * 0.5)), 40, H);
  return {
    x: clamp(Math.round(num(l.x, 0)), 0, W - width),
    y: clamp(Math.round(num(l.y, 0)), 0, H - height),
    width,
    height,
    frame: normFrame(l.frame),
    shadow: normShadow(l.shadow),
  };
}

export function normBackground(b) {
  const x = b || {};
  const g = x.gradient || {};
  const t = x.tint || {};
  const p = x.pattern || {};
  return {
    type: BG_TYPES.some((o) => o.value === x.type) ? x.type : DEFAULT_BACKGROUND.type,
    color: hex(x.color, DEFAULT_BACKGROUND.color),
    gradient: {
      type: g.type === 'radial' ? 'radial' : 'linear',
      angle: clamp(num(g.angle, 135), 0, 360),
      from: hex(g.from, DEFAULT_BACKGROUND.gradient.from),
      to: hex(g.to, DEFAULT_BACKGROUND.gradient.to),
    },
    url: typeof x.url === 'string' ? x.url : '',
    tint: { color: hex(t.color, '#000000'), opacity: clamp(num(t.opacity, 0), 0, 1) },
    pattern: {
      kind: PATTERNS.some((o) => o.value === p.kind) ? p.kind : 'none',
      color: hex(p.color, '#ffffff'),
      opacity: clamp(num(p.opacity, 0.06), 0, 1),
      size: clamp(num(p.size, 64), 8, 400),
      thickness: clamp(num(p.thickness, 2), 1, 40),
    },
    vignette: clamp(num(x.vignette, 0), 0, 1),
  };
}

export function normDecor(list) {
  return (Array.isArray(list) ? list : [])
    .filter((l) => l && typeof l === 'object' && l.type)
    .map((l) => ({
      ...l,
      id: l.id || newLayerId(l.type),
      placement: l.placement === 'under' ? 'under' : 'over',
      scope: ['narration', 'clip'].includes(l.scope) ? l.scope : 'both',
    }));
}

export function normalizeDesign(d, W, H) {
  const x = d || {};
  const kind = ['full_avatar', 'full_content'].includes(x.layoutKind) ? x.layoutKind : 'composite';
  const layers = x.layers || {};
  return {
    canvasId: x.canvasId || 'custom',
    name: x.name || 'Custom canvas',
    layoutKind: kind,
    layers: {
      content: kind === 'full_avatar' ? null : normLayer(layers.content || { x: 0, y: 0, width: W, height: H }, W, H),
      avatar: kind === 'full_content' ? null : normLayer(layers.avatar || { x: 0, y: 0, width: W, height: H }, W, H),
      clip: normLayer(layers.clip || defaultClipBox(W, H), W, H),
    },
    contentFit: FIT_MODES.some((f) => f.value === x.contentFit) ? x.contentFit : 'cover',
    clipFit: FIT_MODES.some((f) => f.value === x.clipFit) ? x.clipFit : 'contain',
    background: normBackground(x.background),
    decor: normDecor(x.decor),
  };
}

export function defaultClipBox(W, H) {
  const w = Math.round(W * 0.72);
  const h = Math.round(H * 0.72);
  return { x: Math.round((W - w) / 2), y: Math.round((H - h) / 2), width: w, height: h };
}

/* ─────────────────────────────────── geometry shared with backend ── */
/** Effective outer corner radius in px (CSS clamps to half the short side). */
export function cornerRadius(frame, w, h) {
  if (frame.shape === 'circle') return Math.min(w, h) / 2;
  return Math.max(0, Math.min(frame.radius, w / 2, h / 2));
}

/** Frame strokes → [{inset, width, dash, dots}] — same as frame_strokes() in Python. */
export function frameStrokes(frame, w, h) {
  const bw = Math.min(frame.width, w / 2, h / 2);
  if (!(bw > 0)) return [];
  if (frame.style === 'double' && bw >= 3) {
    const t = bw / 3;
    return [{ inset: t / 2, width: t, dash: null, dots: false }, { inset: bw - t / 2, width: t, dash: null, dots: false }];
  }
  if (frame.style === 'dashed') return [{ inset: bw / 2, width: bw, dash: [3 * bw, 2 * bw], dots: false }];
  if (frame.style === 'dotted') return [{ inset: bw / 2, width: bw, dash: [0, 2 * bw], dots: true }];
  return [{ inset: bw / 2, width: bw, dash: null, dots: false }];
}

/** Which boxes are visible in a scene mode (mirrors mode_layers()). */
export function modeLayerKeys(design, mode) {
  if (mode === 'clip') return design.layers.clip ? ['clip'] : [];
  const keys = [];
  if (design.layoutKind !== 'full_avatar' && design.layers.content) keys.push('content');
  if (design.layoutKind !== 'full_content' && design.layers.avatar) keys.push('avatar');
  return keys;
}

export function decorFor(design, placement, mode) {
  return (design.decor || [])
    .filter((l) => l.visible !== false && (l.placement || 'over') === placement)
    .filter((l) => (l.scope || 'both') === 'both' || l.scope === mode)
    .sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));
}

/* ─────────────────────────────────────────────── decor factories ── */
export function textDecor(o = {}) {
  return createLayer('text', {
    placement: 'over',
    scope: 'both',
    textTransform: 'none',
    shadowEnabled: false,
    letterSpacing: 0.02,
    fontFamily: 'Segoe UI',
    fontWeight: '700',
    ...o,
  });
}

export function shapeDecor(o = {}) {
  return createLayer('shape', { placement: 'under', scope: 'both', ...o });
}

export const DECOR_KITS = [
  {
    id: 'name_tag',
    label: 'Name tag',
    build: (H) => [
      textDecor({
        name: 'Name tag',
        text: 'Your Name',
        anchor: 'top-left',
        xPct: 4,
        yPct: 82,
        widthPct: 22,
        heightPct: 7,
        fontSizePct: H > 1500 ? 0.022 : 0.034,
        color: '#0b1020',
        bgEnabled: true,
        bgColor: '#ffffff',
        bgOpacity: 1,
        bgRadiusPct: 0.5,
        zIndex: 20,
      }),
    ],
  },
  {
    id: 'live_badge',
    label: 'LIVE badge',
    build: (H) => [
      textDecor({
        name: 'LIVE badge',
        text: 'LIVE',
        anchor: 'top-left',
        xPct: 3,
        yPct: 4,
        widthPct: 9,
        heightPct: 5.5,
        fontSizePct: H > 1500 ? 0.02 : 0.03,
        fontFamily: 'Arial Black',
        fontWeight: '800',
        letterSpacing: 0.12,
        bgEnabled: true,
        bgColor: '#ef4444',
        bgOpacity: 1,
        bgRadiusPct: 0.25,
        zIndex: 22,
      }),
    ],
  },
  {
    id: 'lower_third',
    label: 'Lower third',
    build: (H) => [
      shapeDecor({
        name: 'Lower third bar',
        anchor: 'top-left',
        xPct: 0,
        yPct: 84,
        widthPct: 100,
        heightPct: 9,
        color: '#e11d48',
        placement: 'over',
        zIndex: 18,
      }),
      textDecor({
        name: 'Lower third text',
        text: 'Your headline goes here',
        anchor: 'top-left',
        xPct: 3,
        yPct: 84,
        widthPct: 80,
        heightPct: 9,
        textAlign: 'left',
        fontSizePct: H > 1500 ? 0.026 : 0.04,
        fontFamily: 'Arial Black',
        fontWeight: '800',
        textTransform: 'uppercase',
        zIndex: 19,
      }),
    ],
  },
  {
    id: 'title',
    label: 'Title text',
    build: (H) => [
      textDecor({
        name: 'Title',
        text: 'Episode title',
        anchor: 'top-left',
        xPct: 5,
        yPct: 5,
        widthPct: 60,
        heightPct: 8,
        textAlign: 'left',
        fontSizePct: H > 1500 ? 0.03 : 0.05,
        fontFamily: 'Arial Black',
        fontWeight: '800',
        shadowEnabled: true,
        zIndex: 21,
      }),
    ],
  },
  {
    id: 'accent_bar',
    label: 'Accent bar',
    build: () => [shapeDecor({ name: 'Accent bar', anchor: 'top-left', xPct: 4, yPct: 92, widthPct: 14, heightPct: 0.8, color: '#a78bfa', borderRadiusPct: 0.5, zIndex: 5 })],
  },
  {
    id: 'shape',
    label: 'Shape',
    build: () => [shapeDecor({ name: 'Shape', color: '#7c3aed', borderRadiusPct: 0.12, widthPct: 20, heightPct: 16, zIndex: 6 })],
  },
  {
    id: 'image',
    label: 'Image',
    // a picture cut to a shape — a square box so circles / stars stay round
    build: (H, W = (H * 16) / 9) => [createLayer('image', {
      name: 'Image',
      placement: 'over',
      scope: 'both',
      imageShape: 'circle',
      xPct: 80,
      yPct: 30,
      widthPct: ((H * 0.3) / W) * 100,
      heightPct: 30,
      anchor: 'center',
      borderWidth: 6,
      borderColor: '#ffffff',
      zIndex: 24,
    })],
  },
  {
    id: 'text',
    label: 'Text',
    build: (H) => [textDecor({ name: 'Text', text: 'Your text', widthPct: 40, heightPct: 8, fontSizePct: H > 1500 ? 0.026 : 0.045, zIndex: 23 })],
  },
];

/* ───────────────────────────────────────────── premade canvases ── */
const L = (W, H, x, y, w, h, extra = {}) => ({
  x: Math.round(W * x), y: Math.round(H * y), width: Math.round(W * w), height: Math.round(H * h), ...extra,
});
/** Box with an exact aspect ratio: width as fraction of W, centred on (cx, cy) fractions. */
const AR = (W, H, cx, cy, wFrac, ar, extra = {}) => {
  const width = Math.round(W * wFrac);
  const height = Math.round(width / ar);
  return { x: Math.round(W * cx - width / 2), y: Math.round(H * cy - height / 2), width, height, ...extra };
};
/** Square box sized from min(W,H). */
const SQ = (W, H, x, y, s, extra = {}) => {
  const side = Math.round(Math.min(W, H) * s);
  return { x: Math.round(W * x), y: Math.round(H * y), width: side, height: side, ...extra };
};
const frame = (o) => ({ ...DEFAULT_FRAME, ...o });
const shadow = (o) => ({ ...DEFAULT_SHADOW, enabled: true, ...o });
const bgGrad = (from, to, angle = 135, extra = {}) => ({
  ...clone(DEFAULT_BACKGROUND), type: 'gradient', gradient: { type: 'linear', angle, from, to }, ...extra,
});
const bgRadial = (from, to, extra = {}) => ({
  ...clone(DEFAULT_BACKGROUND), type: 'gradient', gradient: { type: 'radial', angle: 0, from, to }, ...extra,
});
const bgSolid = (color, extra = {}) => ({ ...clone(DEFAULT_BACKGROUND), type: 'solid', color, ...extra });
const pattern = (kind, color, opacity, size, thickness = 2) => ({ kind, color, opacity, size, thickness });
const fs = (H, land, port) => (H > 1500 ? port : land);

export const PREMADE_CANVASES = [
  {
    id: 'studio_split',
    name: 'Studio Split',
    tag: 'Talk show',
    orients: ['landscape', 'portrait', 'square'],
    build(W, H, o) {
      const port = o !== 'landscape';
      return {
        layoutKind: 'composite',
        layers: port ? {
          content: L(W, H, 0.05, o === 'square' ? 0.06 : 0.05, 0.9, o === 'square' ? 0.5 : 0.42, { frame: frame({ radius: 32 }), shadow: shadow({ blur: 60, y: 22, opacity: 0.55 }) }),
          avatar: L(W, H, o === 'square' ? 0.55 : 0.05, o === 'square' ? 0.6 : 0.5, o === 'square' ? 0.4 : 0.9, o === 'square' ? 0.34 : 0.4, { frame: frame({ radius: 32, width: 5, color: '#c4b5fd' }), shadow: shadow({ blur: 60, y: 22, opacity: 0.55 }) }),
          clip: L(W, H, 0.05, port && o !== 'square' ? 0.22 : 0.12, 0.9, port && o !== 'square' ? 0.56 : 0.76, { frame: frame({ radius: 32 }), shadow: shadow({ blur: 60, y: 22, opacity: 0.55 }) }),
        } : {
          content: AR(W, H, 0.355, 0.47, 0.62, 16 / 9, { frame: frame({ radius: 28 }), shadow: shadow({ blur: 64, y: 24, opacity: 0.55 }) }),
          avatar: L(W, H, 0.7, 0.135, 0.26, 0.67, { frame: frame({ radius: 28, width: 5, color: '#c4b5fd' }), shadow: shadow({ blur: 64, y: 24, opacity: 0.55 }) }),
          clip: AR(W, H, 0.5, 0.5, 0.8, 16 / 9, { frame: frame({ radius: 28 }), shadow: shadow({ blur: 64, y: 24, opacity: 0.55 }) }),
        },
        background: bgGrad('#0b1022', '#3b2a8f', 135, { pattern: pattern('dots', '#ffffff', 0.07, 44, 2), vignette: 0.35 }),
        decor: [
          textDecor({
            name: 'Name tag', text: 'Your Name', anchor: 'top-left',
            ...(port
              ? { xPct: o === 'square' ? 55 : 8, yPct: o === 'square' ? 89 : 86.5, widthPct: o === 'square' ? 40 : 46, heightPct: o === 'square' ? 6.5 : 4.2 }
              : { xPct: 72, yPct: 75, widthPct: 22, heightPct: 7 }),
            fontSizePct: fs(H, 0.032, 0.02), color: '#1e1b4b', bgEnabled: true, bgColor: '#ffffff', bgOpacity: 1, bgRadiusPct: 0.5, scope: 'narration', zIndex: 20,
          }),
          textDecor({
            name: 'LIVE badge', text: 'LIVE', anchor: 'top-left', xPct: port ? 5 : 3.5, yPct: port ? (o === 'square' ? 1.2 : 1.2) : 4.5, widthPct: port ? 14 : 7, heightPct: port ? 3 : 5,
            fontSizePct: fs(H, 0.028, 0.017), fontFamily: 'Arial Black', fontWeight: '800', letterSpacing: 0.14, bgEnabled: true, bgColor: '#ef4444', bgOpacity: 1, bgRadiusPct: 0.3, zIndex: 21,
          }),
        ],
      };
    },
  },
  {
    id: 'newsroom',
    name: 'Newsroom',
    tag: 'News',
    orients: ['landscape', 'portrait', 'square'],
    build(W, H, o) {
      const land = o === 'landscape';
      return {
        layoutKind: 'composite',
        layers: land ? {
          content: L(W, H, 0.025, 0.045, 0.695, 0.745, { frame: frame({ width: 4, color: '#e11d48', radius: 6 }) }),
          avatar: L(W, H, 0.735, 0.045, 0.24, 0.745, { frame: frame({ width: 4, color: '#f8fafc', radius: 6 }) }),
          clip: L(W, H, 0.025, 0.045, 0.95, 0.745, { frame: frame({ width: 4, color: '#e11d48', radius: 6 }) }),
        } : {
          content: L(W, H, 0.04, 0.04, 0.92, o === 'square' ? 0.5 : 0.36, { frame: frame({ width: 4, color: '#e11d48', radius: 6 }) }),
          avatar: L(W, H, 0.04, o === 'square' ? 0.56 : 0.42, o === 'square' ? 0.44 : 0.92, o === 'square' ? 0.27 : 0.4, { frame: frame({ width: 4, color: '#f8fafc', radius: 6 }) }),
          clip: L(W, H, 0.04, 0.04, 0.92, o === 'square' ? 0.79 : 0.78, { frame: frame({ width: 4, color: '#e11d48', radius: 6 }) }),
        },
        background: bgSolid('#0a0f1c', { pattern: pattern('diagonal', '#e11d48', 0.06, 28, 3), vignette: 0.25 }),
        decor: [
          shapeDecor({ name: 'Red bar', anchor: 'top-left', xPct: 0, yPct: land ? 82 : (o === 'square' ? 85 : 84), widthPct: 100, heightPct: land ? 8.5 : (o === 'square' ? 7 : 5), color: '#e11d48', placement: 'over', zIndex: 10 }),
          textDecor({ name: 'Breaking', text: 'BREAKING NEWS', anchor: 'top-left', xPct: 2.5, yPct: land ? 82 : (o === 'square' ? 85 : 84), widthPct: 60, heightPct: land ? 8.5 : (o === 'square' ? 7 : 5), textAlign: 'left', fontFamily: 'Arial Black', fontWeight: '800', fontSizePct: fs(H, 0.042, 0.024), letterSpacing: 0.06, zIndex: 11 }),
          shapeDecor({ name: 'Ticker', anchor: 'top-left', xPct: 0, yPct: land ? 90.5 : (o === 'square' ? 92 : 89), widthPct: 100, heightPct: land ? 9.5 : (o === 'square' ? 8 : 5), color: '#f8fafc', placement: 'over', zIndex: 10 }),
          textDecor({ name: 'Ticker text', text: 'Your headline goes here — add the story in one line', anchor: 'top-left', xPct: 2.5, yPct: land ? 90.5 : (o === 'square' ? 92 : 89), widthPct: 95, heightPct: land ? 9.5 : (o === 'square' ? 8 : 5), textAlign: 'left', color: '#0f172a', fontFamily: 'Segoe UI', fontWeight: '700', fontSizePct: fs(H, 0.032, 0.019), zIndex: 11 }),
        ],
      };
    },
  },
  {
    id: 'circle_cam',
    name: 'Circle Cam',
    tag: 'Picture-in-picture',
    orients: ['landscape', 'portrait', 'square'],
    build(W, H, o) {
      const side = o === 'landscape' ? 0.36 : o === 'square' ? 0.34 : 0.4;
      const cam = SQ(W, H, 0, 0, side, { frame: frame({ shape: 'circle', width: 8, color: '#ffffff' }), shadow: shadow({ blur: 44, y: 14, opacity: 0.6 }) });
      cam.x = Math.round(W * 0.035);
      cam.y = Math.round(H - cam.height - (o === 'portrait' ? H * 0.08 : H * 0.06));
      return {
        layoutKind: 'composite',
        layers: {
          content: { x: 0, y: 0, width: W, height: H, frame: frame({}) },
          avatar: cam,
          clip: { x: 0, y: 0, width: W, height: H, frame: frame({}) },
        },
        background: { ...clone(DEFAULT_BACKGROUND), type: 'blurred' },
        decor: [
          textDecor({
            name: 'Name tag', text: 'Host Name', anchor: 'top-left',
            xPct: ((cam.x + cam.width * 0.78) / W) * 100,
            yPct: ((cam.y + cam.height * 0.7) / H) * 100,
            widthPct: o === 'landscape' ? 16 : 34, heightPct: o === 'portrait' ? 3.6 : 6.2,
            fontSizePct: fs(H, 0.03, 0.019), color: '#ffffff', bgEnabled: true, bgColor: '#7c3aed', bgOpacity: 1, bgRadiusPct: 0.5, scope: 'narration', zIndex: 20,
          }),
        ],
      };
    },
  },
  {
    id: 'podcast',
    name: 'Podcast',
    tag: 'Side by side',
    orients: ['landscape', 'portrait', 'square'],
    build(W, H, o) {
      const land = o === 'landscape';
      return {
        layoutKind: 'composite',
        layers: land ? {
          avatar: L(W, H, 0.04, 0.13, 0.44, 0.74, { frame: frame({ radius: 26, width: 3, color: '#fdba74' }), shadow: shadow({ blur: 54, y: 20, opacity: 0.5 }) }),
          content: L(W, H, 0.52, 0.13, 0.44, 0.74, { frame: frame({ radius: 26 }), shadow: shadow({ blur: 54, y: 20, opacity: 0.5 }) }),
          clip: AR(W, H, 0.5, 0.52, 0.78, 16 / 9, { frame: frame({ radius: 26 }), shadow: shadow({ blur: 54, y: 20, opacity: 0.5 }) }),
        } : {
          content: L(W, H, 0.06, o === 'square' ? 0.12 : 0.1, 0.42, o === 'square' ? 0.74 : 0.38, { frame: frame({ radius: 26 }), shadow: shadow({ blur: 54, y: 20, opacity: 0.5 }) }),
          avatar: L(W, H, 0.52, o === 'square' ? 0.12 : 0.1, 0.42, o === 'square' ? 0.74 : 0.38, { frame: frame({ radius: 26, width: 3, color: '#fdba74' }), shadow: shadow({ blur: 54, y: 20, opacity: 0.5 }) }),
          clip: L(W, H, 0.06, o === 'square' ? 0.12 : 0.1, 0.88, o === 'square' ? 0.74 : 0.38, { frame: frame({ radius: 26 }), shadow: shadow({ blur: 54, y: 20, opacity: 0.5 }) }),
        },
        background: bgRadial('#3b1d0e', '#0c0a09', { pattern: pattern('scanlines', '#ffffff', 0.035, 6, 1), vignette: 0.3 }),
        decor: [
          textDecor({ name: 'Episode', text: 'EP. 01', anchor: 'top-left', xPct: land ? 4 : 6, yPct: land ? 3.2 : (o === 'square' ? 2.5 : 4), widthPct: land ? 9 : 20, heightPct: land ? 6 : (o === 'square' ? 6.5 : 3), fontSizePct: fs(H, 0.03, 0.018), fontFamily: 'Arial Black', fontWeight: '800', letterSpacing: 0.12, color: '#1c1917', bgEnabled: true, bgColor: '#fdba74', bgOpacity: 1, bgRadiusPct: 0.25, zIndex: 20 }),
          textDecor({ name: 'Show title', text: 'The Weekly Talk', anchor: 'top-left', xPct: land ? 14 : 28, yPct: land ? 3.2 : (o === 'square' ? 2.5 : 4), widthPct: land ? 50 : 66, heightPct: land ? 6 : (o === 'square' ? 6.5 : 3), textAlign: 'left', fontSizePct: fs(H, 0.036, 0.022), fontFamily: 'Georgia', fontWeight: '700', color: '#fff7ed', zIndex: 20 }),
          ...(land || o === 'portrait' ? [shapeDecor({ name: 'Accent bar', anchor: 'top-left', xPct: land ? 4 : 6, yPct: land ? 91 : 51, widthPct: land ? 92 : 88, heightPct: land ? 0.6 : 0.35, color: '#fdba74', borderRadiusPct: 0.5, zIndex: 4 })] : []),
        ],
      };
    },
  },
  {
    id: 'neon_edge',
    name: 'Neon Edge',
    tag: 'Gaming · tech',
    orients: ['landscape', 'portrait', 'square'],
    build(W, H, o) {
      const land = o === 'landscape';
      const cyan = { frame: frame({ radius: 18, width: 4, color: '#22d3ee' }), shadow: shadow({ blur: 46, y: 0, color: '#22d3ee', opacity: 0.75 }) };
      const pink = { frame: frame({ radius: 18, width: 4, color: '#f0abfc' }), shadow: shadow({ blur: 46, y: 0, color: '#d946ef', opacity: 0.75 }) };
      return {
        layoutKind: 'composite',
        layers: land ? {
          content: AR(W, H, 0.37, 0.5, 0.64, 16 / 9, cyan),
          avatar: L(W, H, 0.735, 0.2, 0.23, 0.6, pink),
          clip: AR(W, H, 0.5, 0.5, 0.78, 16 / 9, cyan),
        } : {
          content: L(W, H, 0.06, 0.06, 0.88, o === 'square' ? 0.5 : 0.38, cyan),
          avatar: L(W, H, o === 'square' ? 0.3 : 0.15, o === 'square' ? 0.62 : 0.5, o === 'square' ? 0.4 : 0.7, o === 'square' ? 0.33 : 0.42, pink),
          clip: L(W, H, 0.06, o === 'square' ? 0.1 : 0.2, 0.88, o === 'square' ? 0.8 : 0.6, cyan),
        },
        background: bgRadial('#2a0a4a', '#05010d', { pattern: pattern('grid', '#d946ef', 0.09, land ? 80 : 72, 2), vignette: 0.55 }),
        decor: [
          textDecor({ name: 'Handle', text: '@yourchannel', anchor: 'top-left', xPct: land ? 73.5 : (o === 'square' ? 30 : 15), yPct: land ? 82.5 : (o === 'square' ? 96 : 93.5), widthPct: land ? 23 : (o === 'square' ? 40 : 70), heightPct: land ? 5 : (o === 'square' ? 3.5 : 3), fontSizePct: fs(H, 0.03, 0.02), color: '#f0abfc', fontFamily: 'Trebuchet MS', fontWeight: '700', letterSpacing: 0.06, zIndex: 20 }),
        ],
      };
    },
  },
  {
    id: 'explainer',
    name: 'Explainer Board',
    tag: 'Education',
    orients: ['landscape', 'portrait', 'square'],
    build(W, H, o) {
      const land = o === 'landscape';
      const ink = '#111827';
      const hard = shadow({ blur: 0, x: 12, y: 12, color: ink, opacity: 1 });
      const cam = SQ(W, H, 0, 0, land ? 0.34 : o === 'square' ? 0.3 : 0.36, { frame: frame({ shape: 'circle', width: 6, color: ink }), shadow: hard });
      cam.x = Math.round(W - cam.width - W * (land ? 0.04 : 0.06));
      cam.y = Math.round(H - cam.height - H * (land ? 0.07 : 0.05));
      return {
        layoutKind: 'composite',
        layers: {
          content: land
            ? AR(W, H, 0.38, 0.54, 0.64, 16 / 9, { frame: frame({ width: 6, color: ink, radius: 14 }), shadow: hard })
            : L(W, H, 0.06, o === 'square' ? 0.14 : 0.12, 0.88, o === 'square' ? 0.46 : 0.44, { frame: frame({ width: 6, color: ink, radius: 14 }), shadow: hard }),
          avatar: cam,
          clip: land
            ? AR(W, H, 0.5, 0.54, 0.8, 16 / 9, { frame: frame({ width: 6, color: ink, radius: 14 }), shadow: hard })
            : L(W, H, 0.06, o === 'square' ? 0.14 : 0.16, 0.88, o === 'square' ? 0.72 : 0.66, { frame: frame({ width: 6, color: ink, radius: 14 }), shadow: hard }),
        },
        background: bgSolid('#f5f1e8', { pattern: pattern('grid', ink, 0.07, land ? 54 : 60, 2) }),
        decor: [
          shapeDecor({ name: 'Highlighter', anchor: 'top-left', xPct: land ? 5.5 : 6, yPct: land ? 3.6 : (o === 'square' ? 3.4 : 4), widthPct: land ? 34 : 60, heightPct: land ? 7 : (o === 'square' ? 7 : 4), color: '#fde047', borderRadiusPct: 0.2, rotation: -1.5, zIndex: 2 }),
          textDecor({ name: 'Topic', text: 'Topic title', anchor: 'top-left', xPct: land ? 6.5 : 8, yPct: land ? 3.6 : (o === 'square' ? 3.4 : 4), widthPct: land ? 33 : 58, heightPct: land ? 7 : (o === 'square' ? 7 : 4), textAlign: 'left', color: ink, fontFamily: 'Arial Black', fontWeight: '800', fontSizePct: fs(H, 0.044, 0.026), zIndex: 21 }),
        ],
      };
    },
  },
  {
    id: 'cinema',
    name: 'Cinema',
    tag: 'Documentary',
    orients: ['landscape', 'portrait', 'square'],
    build(W, H, o) {
      const land = o === 'landscape';
      const content = land ? L(W, H, 0, 0.12, 1, 0.76, { frame: frame({}) }) : AR(W, H, 0.5, o === 'square' ? 0.42 : 0.38, 1, 16 / 9, { frame: frame({}) });
      const cam = land
        ? L(W, H, 0.82, 0.16, 0.15, 0.36, { frame: frame({ radius: 12, width: 2, color: '#ffffff', opacity: 0.7 }), shadow: shadow({ blur: 30, y: 10, opacity: 0.6 }) })
        : L(W, H, 0.3, o === 'square' ? 0.72 : 0.62, 0.4, o === 'square' ? 0.24 : 0.28, { frame: frame({ radius: 12, width: 2, color: '#ffffff', opacity: 0.7 }), shadow: shadow({ blur: 30, y: 10, opacity: 0.6 }) });
      return {
        layoutKind: 'composite',
        layers: { content, avatar: cam, clip: land ? L(W, H, 0, 0.12, 1, 0.76, { frame: frame({}) }) : AR(W, H, 0.5, 0.45, 1, 16 / 9, { frame: frame({}) }) },
        background: bgSolid('#000000', { vignette: 0.4 }),
        decor: [
          textDecor({ name: 'Chapter', text: 'CHAPTER ONE', anchor: 'top-left', xPct: land ? 4 : 6, yPct: land ? 3.5 : (o === 'square' ? 5 : 8), widthPct: land ? 40 : 88, heightPct: land ? 5 : 4, textAlign: land ? 'left' : 'center', color: '#e5e7eb', fontFamily: 'Georgia', fontWeight: '400', letterSpacing: 0.4, fontSizePct: fs(H, 0.026, 0.018), zIndex: 20 }),
        ],
      };
    },
  },
  {
    id: 'minimal_frame',
    name: 'Minimal Frame',
    tag: 'Clean',
    orients: ['landscape', 'portrait', 'square'],
    build(W, H, o) {
      const m = Math.round(Math.min(W, H) * 0.035);
      const cam = o === 'landscape'
        ? L(W, H, 0.765, 0.6, 0.19, 0.3, { frame: frame({ radius: 18, width: 3, color: '#ffffff' }), shadow: shadow({ blur: 36, y: 12, opacity: 0.55 }) })
        : L(W, H, 0.56, o === 'square' ? 0.6 : 0.68, 0.36, o === 'square' ? 0.32 : 0.24, { frame: frame({ radius: 18, width: 3, color: '#ffffff' }), shadow: shadow({ blur: 36, y: 12, opacity: 0.55 }) });
      return {
        layoutKind: 'composite',
        layers: {
          content: { x: m, y: m, width: W - 2 * m, height: H - 2 * m, frame: frame({ radius: 22 }) },
          avatar: cam,
          clip: { x: m, y: m, width: W - 2 * m, height: H - 2 * m, frame: frame({ radius: 22 }) },
        },
        background: bgSolid('#111113'),
        decor: [],
      };
    },
  },
  {
    id: 'magazine',
    name: 'Magazine',
    tag: 'Editorial',
    orients: ['landscape', 'portrait', 'square'],
    build(W, H, o) {
      const land = o === 'landscape';
      return {
        layoutKind: 'composite',
        layers: land ? {
          avatar: L(W, H, 0, 0, 0.4, 1, { frame: frame({}) }),
          content: L(W, H, 0.44, 0.2, 0.52, 0.66, { frame: frame({ radius: 18 }), shadow: shadow({ blur: 50, y: 18, opacity: 0.5 }) }),
          clip: L(W, H, 0.44, 0.2, 0.52, 0.66, { frame: frame({ radius: 18 }), shadow: shadow({ blur: 50, y: 18, opacity: 0.5 }) }),
        } : {
          avatar: L(W, H, 0, o === 'square' ? 0.5 : 0.5, 1, 0.5, { frame: frame({}) }),
          content: L(W, H, 0.06, o === 'square' ? 0.13 : 0.11, 0.88, o === 'square' ? 0.33 : 0.34, { frame: frame({ radius: 18 }), shadow: shadow({ blur: 50, y: 18, opacity: 0.5 }) }),
          clip: L(W, H, 0.06, o === 'square' ? 0.13 : 0.11, 0.88, o === 'square' ? 0.33 : 0.34, { frame: frame({ radius: 18 }), shadow: shadow({ blur: 50, y: 18, opacity: 0.5 }) }),
        },
        background: bgGrad('#111827', '#1f2937', 160),
        decor: [
          shapeDecor({ name: 'Rule', anchor: 'top-left', xPct: land ? 40 : 0, yPct: land ? 0 : 50, widthPct: land ? 0.5 : 100, heightPct: land ? 100 : 0.4, color: '#f59e0b', zIndex: 8, placement: 'over', scope: 'narration' }),
          textDecor({ name: 'Kicker', text: 'IN FOCUS', anchor: 'top-left', xPct: land ? 44 : 6, yPct: land ? 6 : 3, widthPct: land ? 30 : 50, heightPct: land ? 5 : (o === 'square' ? 5 : 3), textAlign: 'left', color: '#f59e0b', fontFamily: 'Arial Black', fontWeight: '800', letterSpacing: 0.3, fontSizePct: fs(H, 0.026, 0.018), zIndex: 20 }),
          textDecor({ name: 'Headline', text: 'Your segment title', anchor: 'top-left', xPct: land ? 44 : 6, yPct: land ? 10.5 : (o === 'square' ? 7.3 : 6), widthPct: land ? 52 : 88, heightPct: land ? 8 : (o === 'square' ? 5.5 : 4), textAlign: 'left', color: '#f9fafb', fontFamily: 'Georgia', fontWeight: '700', fontSizePct: fs(H, 0.05, 0.03), zIndex: 20 }),
        ],
      };
    },
  },
  {
    id: 'spotlight',
    name: 'Spotlight',
    tag: 'Avatar only',
    orients: ['landscape', 'portrait', 'square'],
    build(W, H, o) {
      const land = o === 'landscape';
      const card = land ? AR(W, H, 0.5, 0.47, 0.38, 0.8) : o === 'square' ? L(W, H, 0.2, 0.08, 0.6, 0.76) : L(W, H, 0.08, 0.08, 0.84, 0.74);
      return {
        layoutKind: 'full_avatar',
        layers: {
          content: null,
          avatar: { ...card, height: Math.min(card.height, Math.round(H * 0.82)), frame: frame({ radius: 30 }), shadow: shadow({ blur: 80, y: 30, opacity: 0.6 }) },
          clip: AR(W, H, 0.5, 0.5, land ? 0.8 : 0.9, land ? 16 / 9 : 16 / 9, { frame: frame({ radius: 30 }), shadow: shadow({ blur: 80, y: 30, opacity: 0.6 }) }),
        },
        background: bgRadial('#1e293b', '#020617', { vignette: 0.45 }),
        decor: [
          textDecor({ name: 'Name', text: 'Your Name', anchor: 'top-left', xPct: land ? 30 : 8, yPct: land ? 89 : (o === 'square' ? 88 : 86), widthPct: land ? 40 : 84, heightPct: land ? 7 : (o === 'square' ? 7 : 4), fontSizePct: fs(H, 0.04, 0.026), color: '#f8fafc', fontFamily: 'Segoe UI', fontWeight: '700', scope: 'narration', zIndex: 20 }),
        ],
      };
    },
  },
  {
    id: 'full_avatar',
    name: 'Full Avatar',
    tag: 'Classic',
    orients: ['landscape', 'portrait', 'square'],
    build(W, H) {
      return {
        layoutKind: 'full_avatar',
        layers: { content: null, avatar: { x: 0, y: 0, width: W, height: H }, clip: defaultClipBox(W, H) },
        background: { ...clone(DEFAULT_BACKGROUND), type: 'blurred' },
        decor: [],
      };
    },
  },
  {
    id: 'full_content',
    name: 'Content Only',
    tag: 'Classic · no avatar',
    orients: ['landscape', 'portrait', 'square'],
    build(W, H) {
      return {
        layoutKind: 'full_content',
        layers: { content: { x: 0, y: 0, width: W, height: H }, avatar: null, clip: { x: 0, y: 0, width: W, height: H } },
        background: { ...clone(DEFAULT_BACKGROUND), type: 'blurred' },
        decor: [],
      };
    },
  },
];

export function premadeFor(orientation) {
  return PREMADE_CANVASES.filter((c) => c.orients.includes(orientation));
}

export function buildPremade(id, W, H) {
  const def = PREMADE_CANVASES.find((c) => c.id === id) || PREMADE_CANVASES[0];
  const o = orientationOf(W, H);
  const raw = def.build(W, H, o);
  // stable decor ids (name-based) so a canvas can be re-flowed to another format
  const seen = {};
  const decor = (raw.decor || []).map((l) => {
    const slug = String(l.name || l.type).toLowerCase().replace(/[^a-z0-9]+/g, '_');
    seen[slug] = (seen[slug] || 0) + 1;
    return { ...l, id: `pm_${def.id}_${slug}_${seen[slug]}` };
  });
  return normalizeDesign({ ...raw, decor, canvasId: def.id, name: def.name }, W, H);
}

/* ─────────────────────────────── faceless canvases (canvas mode) ── */
// A faceless canvas has no avatar: narration visuals sit in the CONTENT box,
// [CLIP:…] segments in the CLIP box. Exported with layout_id 'full_content'.
const PRESENTER_DECOR = /^(name tag|name)$/i;

/** Any design → the same look without an avatar (content box takes the clip box). */
export function facelessDesign(design, W, H) {
  const d = normalizeDesign(design, W, H);
  if (d.layoutKind === 'full_content') return d;
  const clip = d.layers.clip || defaultClipBox(W, H);
  const look = d.layers.content || d.layers.avatar || clip;
  return normalizeDesign({
    ...d,
    layoutKind: 'full_content',
    layers: {
      content: { ...look, x: clip.x, y: clip.y, width: clip.width, height: clip.height },
      avatar: null,
      clip,
    },
    decor: d.decor.filter((l) => !PRESENTER_DECOR.test(String(l.name || ''))),
  }, W, H);
}

/** Premades for faceless videos (canvases built around the avatar are left out). */
const AVATAR_ONLY = ['full_avatar', 'spotlight', 'circle_cam'];
export function facelessPremadeFor(orientation) {
  return premadeFor(orientation).filter((c) => !AVATAR_ONLY.includes(c.id));
}

export function buildFacelessPremade(id, W, H) {
  return facelessDesign(buildPremade(id, W, H), W, H);
}

/* ─────────────────────────────────────── export / import ── */
function legacyBorder(frameObj, W) {
  const f = normFrame(frameObj);
  const k = LEGACY_PREVIEW_W / W;
  return {
    width: Math.round(f.width * k),
    color: f.color,
    radius: Math.round((f.shape === 'circle' ? 9999 : f.radius) * k),
    style: 'solid',
  };
}

function legacyBackground(bg) {
  if (bg.type === 'solid') return bg.color;
  if (bg.type === 'gradient') return bg.gradient.from;
  if (bg.type === 'image') return bg.url ? `image:${bg.url}` : '#0d0d18';
  if (bg.type === 'video') return bg.url ? `video:${bg.url}` : '#0d0d18';
  return 'blurred';
}

const even = (v) => Math.max(2, Math.round(v / 2) * 2); // yuv420 video needs even sizes

function exportLayer(l, W) {
  if (!l) return null;
  return {
    x: Math.round(l.x),
    y: Math.round(l.y),
    width: even(l.width),
    height: even(l.height),
    border: legacyBorder(l.frame, W),
    frame: normFrame(l.frame),
    shadow: normShadow(l.shadow),
  };
}

/** Design → layout_config sent to the renderer (and saved in templates). */
export function buildLayoutConfig(design, outputPreset) {
  const out = OUTPUT_PRESETS[outputPreset] || OUTPUT_PRESETS.landscape;
  const W = out.w;
  const H = out.h;
  const d = normalizeDesign(design, W, H);
  const layoutId = d.layoutKind === 'composite' ? `canvas_${d.canvasId || 'custom'}` : d.layoutKind;
  return {
    layout_id: layoutId,
    output_preset: outputPreset,
    canvas: { width: W, height: H },
    avatar_layer: exportLayer(d.layers.avatar, W),
    content_layer: exportLayer(d.layers.content, W),
    clip_layer: exportLayer(d.layers.clip, W),
    background_layer: { x: 0, y: 0, width: W, height: H },
    background: legacyBackground(d.background),
    content_fit: d.contentFit,
    clip_fit: d.clipFit,
    style_version: TH_STYLE_VERSION,
    canvas_id: d.canvasId,
    canvas_name: d.name,
    design: { background: d.background, decor: d.decor },
  };
}

/** layout_config (v2, or a legacy v1 snapshot) → editable design. */
export function designFromLayoutConfig(cfg, fallbackPreset = 'landscape') {
  if (!cfg || typeof cfg !== 'object') return null;
  const preset = OUTPUT_PRESETS[cfg.output_preset] ? cfg.output_preset : (() => {
    const w = cfg.canvas?.width;
    const h = cfg.canvas?.height;
    if (w && h) return h > w ? 'portrait' : w === h ? 'square' : 'landscape';
    return fallbackPreset;
  })();
  const W = OUTPUT_PRESETS[preset].w;
  const H = OUTPUT_PRESETS[preset].h;
  const lid = cfg.layout_id || '';
  const kind = lid === 'full_avatar' ? 'full_avatar' : lid === 'full_content' ? 'full_content' : 'composite';

  if (Number(cfg.style_version) >= TH_STYLE_VERSION && cfg.design) {
    const take = (l) => (l ? { x: l.x, y: l.y, width: l.width, height: l.height, frame: l.frame, shadow: l.shadow } : null);
    return {
      preset,
      design: normalizeDesign({
        canvasId: cfg.canvas_id || 'custom',
        name: cfg.canvas_name || 'Custom canvas',
        layoutKind: kind,
        layers: { content: take(cfg.content_layer), avatar: take(cfg.avatar_layer), clip: take(cfg.clip_layer) },
        contentFit: cfg.content_fit,
        clipFit: cfg.clip_fit,
        background: cfg.design.background,
        decor: cfg.design.decor,
      }, W, H),
    };
  }

  // v1 → v2: borders were in 560px-preview units
  const k = W / LEGACY_PREVIEW_W;
  const fromBorder = (b) => ({
    width: Math.round(num(b?.width, 0) * k),
    color: hex(b?.color, '#ffffff'),
    radius: Math.round(num(b?.radius, 0) * k),
    style: FRAME_STYLES.some((s) => s.value === b?.style) ? b.style : 'solid',
    shape: 'rect',
  });
  const take1 = (l) => (l ? { x: l.x, y: l.y, width: l.width, height: l.height, frame: fromBorder(l.border) } : null);
  const bgStr = String(cfg.background || 'blurred');
  let background = { ...clone(DEFAULT_BACKGROUND), type: 'blurred' };
  if (/^#[0-9a-f]{6}$/i.test(bgStr)) background = { ...background, type: 'solid', color: bgStr };
  else if (bgStr.startsWith('image:')) background = { ...background, type: 'image', url: bgStr.slice(6) };
  else if (bgStr.startsWith('video:')) background = { ...background, type: 'video', url: bgStr.slice(6) };
  return {
    preset,
    design: normalizeDesign({
      canvasId: 'custom',
      name: 'Saved layout',
      layoutKind: kind,
      layers: { content: take1(cfg.content_layer), avatar: take1(cfg.avatar_layer), clip: take1(cfg.clip_layer) || defaultClipBox(W, H) },
      contentFit: cfg.content_fit,
      clipFit: cfg.clip_fit,
      background,
      decor: [],
    }, W, H),
  };
}

/** Re-fit a design to another output size (boxes & decor scale proportionally). */
export function rescaleDesign(design, fromPreset, toPreset) {
  const a = OUTPUT_PRESETS[fromPreset];
  const b = OUTPUT_PRESETS[toPreset];
  if (!a || !b || fromPreset === toPreset) return design;
  const sx = b.w / a.w;
  const sy = b.h / a.h;
  const s = Math.min(sx, sy);
  const scaleLayer = (l) => (l ? {
    ...l,
    x: l.x * sx,
    y: l.y * sy,
    width: l.frame?.shape === 'circle' ? Math.min(l.width, l.height) * s : l.width * sx,
    height: l.frame?.shape === 'circle' ? Math.min(l.width, l.height) * s : l.height * sy,
    frame: { ...l.frame, width: (l.frame?.width || 0) * s, radius: (l.frame?.radius || 0) * s },
  } : null);
  return normalizeDesign({
    ...design,
    layers: {
      content: scaleLayer(design.layers.content),
      avatar: scaleLayer(design.layers.avatar),
      clip: scaleLayer(design.layers.clip),
    },
    decor: design.decor.map((l) => (l.type === 'text' ? { ...l, fontSizePct: (l.fontSizePct || 0.05) * ((a.h * s) / b.h) } : l)),
  }, b.w, b.h);
}

const DECOR_GEOMETRY = ['anchor', 'xPct', 'yPct', 'widthPct', 'heightPct', 'fontSizePct'];

/**
 * Move a design to another output format without distorting it.
 * Premade-based designs take the premade's layout for the new format and keep
 * every style edit (background, frames, shadows, fit, decor text/colours).
 * Custom designs scale each box into its new region while keeping its shape.
 */
export function reflowDesign(design, fromPreset, toPreset) {
  const a = OUTPUT_PRESETS[fromPreset];
  const b = OUTPUT_PRESETS[toPreset];
  if (!a || !b || fromPreset === toPreset) return design;
  const s = Math.min(b.w / a.w, b.h / a.h);
  const scaleFrame = (f) => ({ ...f, width: (f?.width || 0) * s, radius: (f?.radius || 0) * s });
  const scaleShadow = (sh) => (sh ? { ...sh, blur: (sh.blur || 0) * s, x: (sh.x || 0) * s, y: (sh.y || 0) * s, spread: (sh.spread || 0) * s } : sh);
  const def = PREMADE_CANVASES.find((c) => c.id === design.canvasId && c.orients.includes(orientationOf(b.w, b.h)));

  if (def) {
    const base = buildPremade(def.id, b.w, b.h);
    const layers = {};
    ['content', 'avatar', 'clip'].forEach((k) => {
      const geo = base.layers[k];
      const mine = design.layers[k];
      layers[k] = geo && mine
        ? { ...geo, frame: scaleFrame(mine.frame), shadow: scaleShadow(mine.shadow) }
        : geo;
    });
    const baseById = Object.fromEntries(base.decor.map((l) => [l.id, l]));
    const decor = design.decor.map((l) => {
      const geo = baseById[l.id];
      if (!geo) return l.type === 'text' ? { ...l, fontSizePct: (l.fontSizePct || 0.05) * ((a.h * s) / b.h) } : l;
      const out = { ...l };
      DECOR_GEOMETRY.forEach((key) => { if (key in geo) out[key] = geo[key]; else delete out[key]; });
      return out;
    });
    return normalizeDesign({ ...design, layoutKind: base.layoutKind, layers, decor }, b.w, b.h);
  }

  const sx = b.w / a.w;
  const sy = b.h / a.h;
  const fitLayer = (l) => {
    if (!l) return null;
    const rw = l.width * sx;
    const rh = l.height * sy;
    const ar = l.width / l.height;
    let w = rw;
    let h = w / ar;
    if (h > rh) { h = rh; w = h * ar; }
    return {
      ...l,
      x: l.x * sx + (rw - w) / 2,
      y: l.y * sy + (rh - h) / 2,
      width: w,
      height: h,
      frame: scaleFrame(l.frame),
      shadow: scaleShadow(l.shadow),
    };
  };
  return normalizeDesign({
    ...design,
    layers: {
      content: fitLayer(design.layers.content),
      avatar: fitLayer(design.layers.avatar),
      clip: fitLayer(design.layers.clip),
    },
    decor: design.decor.map((l) => (l.type === 'text' ? { ...l, fontSizePct: (l.fontSizePct || 0.05) * ((a.h * s) / b.h) } : l)),
  }, b.w, b.h);
}

export function cloneDesign(d) {
  return clone(d);
}
